/**
 * ThemeV2EditorView - Reusable V2 theme editor component
 *
 * Orchestrates V2 theme editing with:
 * - Left: V2 Screen preview carousel (V2ScreenView + DeviceMockup)
 * - Right: V2 Settings panel (colors, fonts, options list, background, header, action bar)
 * - Auto-propagation of all settings to StepNode screens via V2 mapper
 *
 * Color propagation:
 * - colors.titlesSubtitles → title fonts (100%), subtitle fonts (50% alpha)
 * - colors.titlesSubtitles → itemTitle (100%), itemSubtitle (50% alpha)
 * - fonts.main.fontFamily → title, subtitle, itemTitle, itemSubtitle
 *
 * After any color/font change, V2 mapper applies to:
 * - actionBar/headerBar (via applyToBasicScreen)
 * - all screens in flowDocument (via applyToFlowDocument)
 */

import React, { useCallback, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ThemeV2PreviewCarousel } from './ThemeV2PreviewCarousel';
import { ThemeV2SettingsPanel } from './ThemeV2SettingsPanel';
import { TransitionPreviewDemo } from './TransitionPreviewDemo';
import { defaultThemeV2Mapper } from '../mappers/DefaultThemeV2Mapper';
import { applyAnimationSettingsToFlowDocument } from '../animation/applyAnimationSettingsToFlowDocument';
import { resolveMotionPresetId } from '../animation/themeAnimationPresets';
import { addAlpha50 } from '../types/themesV2.types';
import type { ThemeV2 } from '../types/themesV2.types';
import type { FontFamily } from '@segmently/schema-models/v2';
import type { StepNode } from '@segmently/schema-models/v2';
import type { PlatformId } from '@modules/font-config';
import { isThemeV2SelectedTemplatePropagationEnabled } from '../themeV2FeatureFlags';

/**
 * Deep clone utility
 */
function deepClone<T>(obj: T): T {
  if (typeof structuredClone === 'function') {
    return structuredClone(obj);
  }
  return JSON.parse(JSON.stringify(obj));
}

function isThemePath(path: string, key: string): boolean {
  return path === key || path.startsWith(`${key}.`);
}

function getValueByPath(obj: unknown, path: string): unknown {
  let current = obj;
  for (const part of path.split('.')) {
    if (!current || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

function setValueByPath(
  obj: Record<string, unknown>,
  path: string,
  value: unknown
): void {
  const parts = path.split('.');
  let current = obj;
  for (let index = 0; index < parts.length - 1; index += 1) {
    if (!current[parts[index]] || typeof current[parts[index]] !== 'object') {
      current[parts[index]] = {};
    }
    current = current[parts[index]] as Record<string, unknown>;
  }
  current[parts[parts.length - 1]] = value;
}

export interface ThemeV2EditorViewProps {
  /** V2 Theme object with FlowDocument to edit */
  theme: ThemeV2;
  /** Callback on any change - returns modified theme */
  onChange: (updatedTheme: ThemeV2) => void;
  /** Whether editing is disabled */
  disabled?: boolean;
  /** Callback when screen is clicked for editing - parent handles dialog */
  onScreenEdit?: (screenId: string, step: StepNode) => void;
  /** Key to force carousel refresh (increment after screen save) */
  carouselKey?: number;
  /** Optional secondary actions rendered under the settings panel */
  settingsPanelFooter?: React.ReactNode;
  /** Project ID used by previews to load per-project font mappings */
  projectId?: string;
  /** Optional platform override for deterministic preview/testing */
  previewPlatform?: PlatformId;
  /** Opt-in: per-tile screen-type selection for selective theme apply */
  selectable?: boolean;
  /** Screen types currently selected for apply */
  selectedScreenTypes?: string[];
  /** Toggle a screen type in the apply selection */
  onToggleScreenType?: (screenType: string) => void;
  /** Replace the whole apply selection (Select/Deselect all) */
  onSetSelectedScreenTypes?: (screenTypes: string[]) => void;
  /**
   * Test/host override for the code-gated selected-template propagation mode.
   * Omitted uses the production feature flag, which defaults to false.
   */
  propagateThemeChangesToSelectedTemplates?: boolean;
  /** Per-tile "Apply for this type" shortcut; passes the tile's template screen id */
  onApplyForType?: (screenType: string, templateScreenId?: string) => void;
}

/**
 * ThemeV2EditorView - Reusable V2 theme editor
 *
 * Handles auto-propagation of colors/fonts to actionBar, headerBar,
 * and all screens in the FlowDocument via defaultThemeV2Mapper.
 */
export const ThemeV2EditorView: React.FC<ThemeV2EditorViewProps> = ({
  theme,
  onChange,
  disabled = false,
  onScreenEdit,
  carouselKey = 0,
  settingsPanelFooter,
  projectId,
  previewPlatform,
  selectable,
  selectedScreenTypes,
  onToggleScreenType,
  onSetSelectedScreenTypes,
  propagateThemeChangesToSelectedTemplates: selectedTemplatePropagationOverride,
  onApplyForType,
}) => {
  const propagateThemeChangesToSelectedTemplates =
    selectedTemplatePropagationOverride
    ?? isThemeV2SelectedTemplatePropagationEnabled();
  // Some shared form controls intentionally memoize without comparing their
  // callback prop. Keep the handler safe even if such a control invokes a
  // callback captured before a template-screen save.
  const themeRef = useRef(theme);
  themeRef.current = theme;
  const propagationScopeRef = useRef({
    enabled: propagateThemeChangesToSelectedTemplates,
    selectedTemplateKeys: selectedScreenTypes,
  });
  propagationScopeRef.current = {
    enabled: propagateThemeChangesToSelectedTemplates,
    selectedTemplateKeys: selectedScreenTypes,
  };

  /**
   * Handle settings change with V2 propagation logic
   */
  const handleSettingsChange = useCallback((path: string, value: unknown) => {
    const previousTheme = themeRef.current;
    const propagationScope = propagationScopeRef.current;
    const deltaOptions = {
      changedThemePath: path,
      ...(propagationScope.enabled &&
      propagationScope.selectedTemplateKeys !== undefined
        ? { selectedTemplateKeys: propagationScope.selectedTemplateKeys }
        : {}),
    };
    const parts = path.split('.');
    const updated = deepClone(previousTheme);
    let current: Record<string, unknown> = updated as unknown as Record<string, unknown>;

    for (let i = 0; i < parts.length - 1; i++) {
      if (!current[parts[i]]) {
        current[parts[i]] = {};
      }
      current = current[parts[i]] as Record<string, unknown>;
    }
    current[parts[parts.length - 1]] = value;

    // When colors.titlesSubtitles changes, propagate to all color fields
    if (path === 'colors.titlesSubtitles') {
      const color = value as string;
      const color50 = addAlpha50(color);

      // Titles: 100% color
      updated.fonts.title.color = color;
      updated.optionsListSettings.itemTitle.color = color;
      // Subtitles: 50% alpha
      updated.fonts.subtitle.color = color50;
      updated.optionsListSettings.itemSubtitle.color = color50;
    }

    // When main font family changes, propagate to all font settings
    if (path === 'fonts.main.fontFamily') {
      const fontFamily = value as FontFamily;
      updated.fonts.title.fontFamily = fontFamily;
      updated.fonts.subtitle.fontFamily = fontFamily;
      updated.optionsListSettings.itemTitle.fontFamily = fontFamily;
      updated.optionsListSettings.itemSubtitle.fontFamily = fontFamily;
    }

    // Auto-propagate colors/fonts to actionBar, headerBar, and all screens
    if (isThemePath(path, 'colors') || isThemePath(path, 'fonts')) {
      const mapped = defaultThemeV2Mapper.applyToBasicScreen(updated as ThemeV2);
      updated.actionBar = mapped.actionBar;
      updated.headerBar = mapped.headerBar;

      // Apply only the authored token delta. When the code-gated mode is on,
      // reuse the existing tile selection as the propagation scope.
      updated.flowDocument = defaultThemeV2Mapper.applyThemeDeltaToFlowDocument(
        previousTheme,
        updated as ThemeV2,
        updated.flowDocument,
        deltaOptions
      );
    }
    // Propagate optionsListSettings changes to screens
    else if (isThemePath(path, 'optionsListSettings')) {
      updated.flowDocument = defaultThemeV2Mapper.applyThemeDeltaToFlowDocument(
        previousTheme,
        updated as ThemeV2,
        updated.flowDocument,
        deltaOptions
      );
    }
    // Materialize motion-scheme changes onto the template FlowDocument:
    // defaultTransition/defaultScreenAnimation + per-template content.screenAnimation
    // (tiles stay WYSIWYG; template-add and theme apply inherit from templates).
    else if (isThemePath(path, 'animationSettings')) {
      if (updated.animationSettings) {
        // motionPreset is derived from the material values — an Advanced edit
        // that diverges from every scheme flips the card selection to Custom.
        updated.animationSettings.motionPreset =
          resolveMotionPresetId(updated.animationSettings) ?? undefined;
      }
      updated.flowDocument = applyAnimationSettingsToFlowDocument(
        updated.animationSettings,
        updated.flowDocument
      );
    }
    // Propagate background/headerBar/actionBar changes to all screens
    else if (
      isThemePath(path, 'background') ||
      isThemePath(path, 'webViewBackgroundColor') ||
      isThemePath(path, 'headerBar') ||
      isThemePath(path, 'actionBar')
    ) {
      // When headerBar/actionBar structure changes (e.g. enabling skip button),
      // seed appearance onto NEWLY-added elements without clobbering colors the
      // user just edited in the panel (seed mode). The palette still re-cascades
      // when colors/fonts change (cascade mode, handled in the colors/fonts branch).
      if (isThemePath(path, 'headerBar') || isThemePath(path, 'actionBar')) {
        const mapped = defaultThemeV2Mapper.applyToBasicScreen(updated as ThemeV2, {
          mode: 'seed',
        });
        const normalizedTheme = {
          headerBar: mapped.headerBar,
          actionBar: mapped.actionBar,
        };

        // Seeding a newly-enabled control may fill that control's missing
        // appearance, but it must not normalize the sibling chrome region or
        // any unrelated leaf within the same region.
        setValueByPath(
          updated as unknown as Record<string, unknown>,
          path,
          getValueByPath(normalizedTheme, path)
        );
      }

      updated.flowDocument = defaultThemeV2Mapper.applyThemeDeltaToFlowDocument(
        previousTheme,
        updated as ThemeV2,
        updated.flowDocument,
        deltaOptions
      );
    }

    onChange(updated as ThemeV2);
  }, [onChange]);

  // ── Clone a tile into a new subtype (theme-editor authoring) ──
  const [cloneSource, setCloneSource] = useState<StepNode | null>(null);
  const [subtypeInput, setSubtypeInput] = useState('');
  const [cloneError, setCloneError] = useState<string | null>(null);

  const openCloneDialog = useCallback((step: StepNode) => {
    setCloneSource(step);
    setSubtypeInput('');
    setCloneError(null);
  }, []);

  const handleConfirmClone = useCallback(() => {
    if (!cloneSource) return;
    const value = subtypeInput.trim();
    if (!value) {
      setCloneError('Enter a subtype name');
      return;
    }
    // Unique subtype per StepKind (matches the picker rule).
    const existing = new Set(
      Object.values(theme.flowDocument?.screens ?? {})
        .filter((s) => s.screenType === cloneSource.screenType)
        .map((s) => ((s as { subtype?: string }).subtype ?? '').trim())
        .filter(Boolean),
    );
    if (existing.has(value)) {
      setCloneError(`Subtype “${value}” already exists for this screen type`);
      return;
    }

    const baseName = (cloneSource.name as string | undefined)?.trim() || (cloneSource.screenType as string);
    const node: StepNode = {
      ...deepClone(cloneSource),
      id: crypto.randomUUID(),
      subtype: value,
      name: `${baseName} (${value})`,
    };

    onChange({
      ...theme,
      flowDocument: {
        ...theme.flowDocument,
        screens: { ...theme.flowDocument.screens, [node.id]: node },
      },
    } as ThemeV2);

    setCloneSource(null);
    setSubtypeInput('');
    setCloneError(null);
    // Auto-open the screen editor on the freshly created template.
    onScreenEdit?.(node.id, node);
  }, [cloneSource, subtypeInput, theme, onChange, onScreenEdit]);

  // ── UXR C9: delete an AUTHORED subtype template (built-ins are locked) ──
  // Removes only the template from the theme's flowDocument map; screens
  // already placed on onboardings are untouched.
  const [deleteSource, setDeleteSource] = useState<StepNode | null>(null);

  // The gallery's "Play animations" also plays the transition demo (owner 2026-09-29).
  const [demoPlayNonce, setDemoPlayNonce] = useState(0);
  const handlePlayAnimations = useCallback(() => setDemoPlayNonce((n) => n + 1), []);

  const handleConfirmDelete = useCallback(() => {
    if (!deleteSource) return;
    const screens = { ...theme.flowDocument?.screens };
    delete screens[deleteSource.id];
    onChange({
      ...theme,
      flowDocument: {
        ...theme.flowDocument,
        screens,
      },
    } as ThemeV2);
    setDeleteSource(null);
  }, [deleteSource, theme, onChange]);

  return (
    <Box
      data-testid="theme-v2-editor-view"
      sx={{
        display: 'flex',
        height: '100%',
        gap: 2,
      }}
    >
      {/* Left: V2 Preview Carousel */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
        }}
      >
        <ThemeV2PreviewCarousel
          key={carouselKey}
          theme={theme}
          onScreenEdit={onScreenEdit}
          projectId={projectId}
          previewPlatform={previewPlatform}
          selectable={selectable}
          selectedScreenTypes={selectedScreenTypes}
          onToggleScreenType={onToggleScreenType}
          onSetSelectedScreenTypes={onSetSelectedScreenTypes}
          onApplyForType={onApplyForType}
          onCloneTemplate={disabled ? undefined : openCloneDialog}
          onDeleteTemplate={disabled ? undefined : setDeleteSource}
          onPlayAnimations={handlePlayAnimations}
        />
      </Box>

      {/* Right: V2 Settings Panel */}
      <Box
        sx={{
          width: 360,
          flexShrink: 0,
          overflow: 'auto',
          borderLeft: 1,
          borderColor: 'divider',
          pl: 2,
        }}
      >
        <ThemeV2SettingsPanel
          theme={theme}
          onChange={handleSettingsChange}
          disabled={disabled}
          animationsDemo={
            <TransitionPreviewDemo transition={theme.animationSettings?.transition} playNonce={demoPlayNonce} />
          }
        />
        {settingsPanelFooter && (
          <Box sx={{ mt: 2, pb: 3 }}>
            {settingsPanelFooter}
          </Box>
        )}
      </Box>

      {/* UXR C9: delete-subtype-template confirm (destructive — no X, explicit Cancel) */}
      <Dialog
        open={Boolean(deleteSource)}
        onClose={() => setDeleteSource(null)}
        maxWidth="xs"
        fullWidth
        data-testid="theme-v2-delete-dialog"
      >
        <DialogTitle>Delete subtype template</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Delete the «{(deleteSource?.subtype ?? '').trim()}» {deleteSource
              ? (deleteSource.screenType as string).replace(/([A-Z])/g, ' $1').trim()
              : ''} template from this theme? Screens already placed on onboardings keep
            working — only the template disappears from the rail and pickers.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteSource(null)} data-testid="theme-v2-delete-cancel">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            data-testid="theme-v2-delete-confirm"
          >
            Delete template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clone-into-subtype dialog */}
      <Dialog
        open={Boolean(cloneSource)}
        onClose={() => setCloneSource(null)}
        maxWidth="xs"
        fullWidth
        data-testid="theme-v2-clone-dialog"
      >
        <DialogTitle>Clone as new subtype</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Adds a new{' '}
              {cloneSource ? (cloneSource.screenType as string).replace(/([A-Z])/g, ' $1').trim() : ''}{' '}
              template next to its parent. It opens for editing right away and is saved with the theme.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Subtype"
              placeholder="e.g. quiz, premium"
              value={subtypeInput}
              onChange={(e) => {
                setSubtypeInput(e.target.value);
                if (cloneError) setCloneError(null);
              }}
              error={Boolean(cloneError)}
              helperText={cloneError ?? 'A short label, unique for this screen type'}
              data-testid="theme-v2-clone-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleConfirmClone();
                }
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneSource(null)} data-testid="theme-v2-clone-cancel">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirmClone} data-testid="theme-v2-clone-confirm">
            Create template
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ThemeV2EditorView;
